import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import { Alert, Link as MuiLink } from '@mui/material';

import OrderSearchForm, { MultiFilterState } from '~/components/Common/Forms/OrderSearchForm';
import Spinner from '~/components/Common/Spinner';
import GiftCardsPage from '~/components/Purchases/Home/GiftCardsPage';

import { ITEM_TYPE_GIFT_CARD } from '~/consts/order';
import { ROLE_ICS_STORE_TEAM } from '~/consts/roles';

import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentUser from '~/hooks/useCurrentUser';
import useQuery from '~/hooks/useQuery';

import { getPurchases } from '~/services/OrdersService';

import { addQuery, removeQuery } from '~/utils/url';

const DEFAULT_SIZE_PER_PAGE = 10;
const SEARCH_BY_CUSTOMER_ID_SIZE_PER_PAGE = 50;
const GIFT_CARD_BALANCE_PATH = '/purchases/section/gift-card-balance';

function isBlackhawkCardCode(code: string) {
  const trimmedCode = code.trim();

  return (trimmedCode.length === 16 || trimmedCode.length === 19) && /^\d+$/.test(trimmedCode);
}

function GiftCardsPageContainer() {
  const { push } = useAppNavigation();
  const location = useLocation();
  const query = useQuery();
  const { user } = useCurrentUser();
  const { enqueueSnackbar } = useSnackbar();
  const brand = useSelector((state: App.State) => state.tenant.brand);

  const [isLoading, setIsLoading] = useState(true);
  const [purchases, setPurchases] = useState(null);
  const [filterField, setFilterField] = useState(null);
  const [filterValue, setFilterValue] = useState(null);
  const [additionalSearchParams, setAdditionalSearchParams] = useState<MultiFilterState>(null);
  const [sizePerPage, setSizePerPage] = useState(DEFAULT_SIZE_PER_PAGE);
  const [showBlackhawkCardHint, setShowBlackhawkCardHint] = useState(false);

  const currentPage = parseInt(query.get('page')) || 1;
  const customerId = query.get('customer_id');

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setPurchases(null);

    if (user?.roles.includes(ROLE_ICS_STORE_TEAM)) {
      // we don't want these users to be able to see all results so
      // if there are no filters selected, just return empty
      if (!filterField || !filterValue) {
        setPurchases({
          result: [],
          total: 0,
        });
        setIsLoading(false);
        return;
      }
    }

    try {
      const fetchedPurchases = await getPurchases({
        page: currentPage,
        per_page: sizePerPage,
        brand,
        customer_id: customerId,
        filterBy: filterField,
        filterValue,
        additionalFilter: additionalSearchParams,
        item_type: ITEM_TYPE_GIFT_CARD,
      });

      if (!fetchedPurchases) {
        enqueueSnackbar('Failed to load data', {
          variant: 'error',
        });
      }

      setPurchases(fetchedPurchases);
    } catch (error) {
      enqueueSnackbar('Failed to load data', {
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    user?.roles,
    filterField,
    filterValue,
    currentPage,
    sizePerPage,
    brand,
    customerId,
    additionalSearchParams,
    enqueueSnackbar,
  ]);

  const onPageChange = (page) => {
    const newLocation = addQuery(location, { page });
    push(newLocation);
  };

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [fetchData, user]);

  useEffect(() => {
    if (customerId) {
      setSizePerPage(SEARCH_BY_CUSTOMER_ID_SIZE_PER_PAGE);
    } else {
      setSizePerPage(DEFAULT_SIZE_PER_PAGE);
    }
  }, [customerId]);

  const searchQuery = (field, value) => {
    if (field === 'gift_card_code' && isBlackhawkCardCode(value)) {
      setShowBlackhawkCardHint(true);
      return;
    }

    setFilterField(field);
    setFilterValue(value);

    const newLocation = removeQuery(location, 'customer_id', 'page');

    push(newLocation);
  };

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        Want to check gift card balance - either LE or BlackHawk?{' '}
        <MuiLink component={Link} to={GIFT_CARD_BALANCE_PATH}>
          Click here
        </MuiLink>
        .
      </Alert>

      <OrderSearchForm
        bookingNumberPlaceholder="Enter Gift Card Code or Order ID"
        bookingNumberError={showBlackhawkCardHint}
        bookingNumberHelperText={
          showBlackhawkCardHint ? (
            <>
              That looks like a BlackHawk card.{' '}
              <MuiLink component={Link} to={GIFT_CARD_BALANCE_PATH}>
                Check balance here
              </MuiLink>
              .
            </>
          ) : undefined
        }
        onBookingNumberChange={(value) => {
          setShowBlackhawkCardHint(isBlackhawkCardCode(value));
        }}
        searchQuery={searchQuery}
        setAdditionalParams={setAdditionalSearchParams}
        e2eSuffix="-gift-card"
        bookingIdType="gift_card_code"
      />
      <>
        {isLoading && <Spinner size={48} />}

        {!isLoading && (
          <GiftCardsPage
            orders={purchases?.result}
            total={purchases?.total}
            page={currentPage}
            sizePerPage={sizePerPage}
            onPageChange={onPageChange}
            rerender={fetchData}
          />
        )}
      </>
    </>
  );
}

export default GiftCardsPageContainer;
